import { useEffect, useState } from "react";
import { makeStyles, Skeleton, SkeletonItem } from "@fluentui/react-components";
import testimonialService from "../../../services/TestimonialService";
import ITestimonial from "../../../models/testimonial/ITestimonial";
import Hero from "../../../components/hero/Hero";
import Testimonial from "./Testimonial";
import { useTranslation } from "react-i18next";
import Status from "../../../enums/Status";
import SEO from "../../../app/SEO";
import clientService from "../../../services/ClientService";
import IClient from "../../../models/client/IClient";

const useStyles = makeStyles({
    mainContainer: {
        padding: 0,
    },
    cardsContainer: {
        display: "flex",
        flexWrap: "wrap",
        rowGap: "16px",
        columnGap: "48px",
        padding: "32px",
        maxWidth: "100%",
        margin: "0 auto",
        justifyContent: "center",

        "@media (min-width: 768px)": {
            maxWidth: "1200px"
        }
    },
    skeleton: {
        width: "100%",
        maxWidth: "500px",
        height: "300px",

        "@media (max-width: 1200px)": {
            maxWidth: "100%",
        }
    },
    skeletonItem: {
        width: "100%",
        height: "100%"
    }
});

export default function Testimonials() {
    const { t } = useTranslation("testimonials");
    const styles = useStyles();

    const [status, setStatus] = useState<Status>(Status.LOADING);
    const [testimonials, setTestimonials] = useState<ITestimonial[]>([]);
    const [clients, setClients] = useState<IClient[]>([]);

    useEffect(() => {
        fetchTestimonials(setTestimonials, setClients, setStatus);
    }, []);

    if (status === Status.LOAD_ERROR) {
        return null;
    }

    return (
        <>
        <SEO
            title={{ pageTitle: t("hero.pageTitle"), title: t("hero.pageTitle") }}
            metaDescription={t("hero.metaDescription")}
            canonicalPath="/testimonials"
        />
        <div className={styles.mainContainer}>
            <Hero
                title={t("hero.title")}
                subtitle={t("hero.subtitle")}
                imageUri="/img/banners/testimonials.webp"
                />

            <div className={styles.cardsContainer}>
                {
                    status === Status.LOADING &&
                    Array.from({ length: 4 }).map((_, index) => (
                        <Skeleton className={styles.skeleton} key={index}>
                            <SkeletonItem className={styles.skeletonItem} />
                        </Skeleton>
                    ))
                }
                
                {
                    status === Status.LOAD_SUCCESS &&
                    testimonials.map((t, i) => (
                        <Testimonial
                            key={i}
                            testimonial={t}
                            client={clients.find((client) => client.id === t.clientId)}
                        />
                    ))
                }
            </div>
        </div>
        </>
    );
}

async function fetchTestimonials(
    setTestimonials: (testimonials: ITestimonial[]) => void,
    setClients: (clients: IClient[]) => void,
    setStatus: (status: Status) => void
) {
    try {
        const [result, clients] = await Promise.all([
            testimonialService.getTestimonials(),
            clientService.getClients()
        ]);

        const sortedTestimonials = [...result].sort(
            (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
        );

        setTestimonials(sortedTestimonials);
        setClients(clients);
        setStatus(Status.LOAD_SUCCESS);
    } catch (_) {
        setStatus(Status.LOAD_ERROR);
    }
}